import {
  ActionIcon,
  Button,
  Drawer,
  EditIcon,
  LoadingLoop,
  Modal,
  StudyIcon,
  Tabs,
  TextInput,
  Toggle,
} from '@medeloop/medeloop-components-library';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';

import {
  UpdateOrganizationDTO,
  getOrganizationById,
  getOrganizationGeneralInfo,
  updateOrganization,
} from 'src/api/organizations';

import {
  OrganizationResearchersTable,
  OrganizationStudiesTable,
  OrganizationDataSourcesTable,
  OrganizationalUnitsTable,
  TooltipButton,
} from 'src/components';
import { selectAccount, selectAuthToken } from 'src/store/selectors';
import { showToast } from 'src/store/toast';
import {
  Organization,
  OrganizationResearcher,
  OrganizationDataSource,
} from 'src/types/organizations';
import { UserGroup } from 'src/types/users';
import { Study } from 'src/types/studies';

type OutletContextType = {
  container: HTMLElement;
};

export const OrganizationDetails = () => {
  const token = useSelector(selectAuthToken);
  const account = useSelector(selectAccount);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { organizationId } = useParams();

  if (!organizationId) {
    navigate(-1);
    return;
  }

  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const isAdmin = useMemo(() => {
    return (
      account.groups.includes(UserGroup.ADMIN) &&
      account.organizationAdminIds.some((id) => organizationId.startsWith(id))
    );
  }, [account]);

  // set container for drawer children
  const [container, setContainer] = useState(document.body);
  const drawerRef = useRef(null);
  useEffect(() => {
    if (drawerRef.current) {
      setContainer(drawerRef.current);
    }
  }, [drawerRef]);

  // set initial state
  const [organizationName, setOrganizationName] = useState('');
  const [organizationDisplayName, setOrganizationDisplayName] = useState('');
  const [orgWideAccess, setOrgWideAccess] = useState(false);
  const [confirmOrgWideAccess, setConfirmOrgWideAccess] = useState(false);

  const isOU = organizationId.includes(':');

  if (!organizationId) {
    navigate(-1);
  }

  const { isLoading, isError, data, refetch } = useQuery<{
    organization: Organization;
    studies: Study[];
    researchers: OrganizationResearcher[];
    admins: OrganizationResearcher[];
    dataSources: OrganizationDataSource[];
    organizationalUnits: Organization[];
  }>({
    queryKey: ['organization', organizationId],
    queryFn: async () => {
      if (organizationId) {
        const result = await getOrganizationById({ token, id: organizationId });

        setOrganizationName(result.organization.name);
        setOrganizationDisplayName(result.organization.displayName);
        setOrgWideAccess(result.organization.orgWideAccess);

        return result;
      }
    },
  });

  const rootOrganizationQuery = useQuery({
    queryKey: ['organization', data?.organization.rootOrganizationId],
    queryFn: () =>
      getOrganizationGeneralInfo({ id: data?.organization.rootOrganizationId || '', token }),
    enabled: isOU,
  });
  const rootOrganization = rootOrganizationQuery.data?.organization;

  // Refetch data when location changes
  const location = useLocation();
  useEffect(() => {
    refetch();
  }, [location]);

  const handleCreateStudy = () => {
    setIsMenuOpen(false);
    navigate(`/overview/organizations/${organizationId}/create-study`);
  };

  const handleInviteResearcher = () => {
    setIsMenuOpen(false);
    navigate(`/overview/organizations/${organizationId}/invite-researcher`);
  };

  const saveOrganizationMutation = useMutation({
    mutationFn: async (updatedOrganization: UpdateOrganizationDTO) => {
      return updateOrganization({ token, updatedOrganization });
    },
    onSuccess: () => {
      dispatch(
        showToast({
          message: t('organization_updated_successfully', { name: data?.organization.displayName }),
          type: 'success',
        }),
      );
      refetch();
    },
    onError: (error: Error) => {
      dispatch(showToast({ message: `${t('error')}: ${error.message}`, type: 'dangerous' }));
    },
  });

  const handleSave = () => {
    if (organizationId) {
      saveOrganizationMutation.mutate({
        organizationId: organizationId,
        name: organizationName,
        displayName: organizationDisplayName,
        orgWideAccess,
      });
      setIsEdit(false);
    }
  };

  const handleExit = () => {
    setIsDrawerOpen(false);
    navigate('/overview');
  };

  return (
    <Drawer
      open={isDrawerOpen}
      onOpenChange={() => {
        setIsEdit(false);
        handleExit();
      }}
    >
      <Drawer.Overlay />
      <Drawer.Content className="w-5/6 overflow-auto" ref={drawerRef}>
        {isLoading && (
          <div className="flex flex-grow items-center justify-center">
            <LoadingLoop className="w-16" />
          </div>
        )}

        {isError && !data && (
          <div className="flex flex-grow items-center justify-center">
            <div className="bg-red-200 text-red-600 p-4 rounded-md">
              {t('error_getting_organization')}
            </div>
          </div>
        )}

        {data && (
          <div className="flex flex-col flex-grow gap-4">
            {/* Title */}
            {isEdit && (
              <Drawer.Header className="!mb-5">
                <div className="flex items-center">
                  <div className="flex flex-row gap-4 w-[512px]">
                    <TextInput
                      label={t('display_name_header')}
                      value={organizationDisplayName}
                      onChange={(e) => setOrganizationDisplayName(e.target.value)}
                    />
                    <TextInput
                      label={t('organization_name')}
                      value={organizationName}
                      onChange={(e) => setOrganizationName(e.target.value)}
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex flex-grow justify-end gap-4">
                    <Button variant="secondary" color="white" onClick={() => setIsEdit(false)}>
                      {t('cancel')}
                    </Button>
                    <Button variant="primary" onClick={handleSave}>
                      {t('save_changes')}
                    </Button>
                  </div>
                </div>
                {isOU && rootOrganization && (
                  <div className="flex pt-4">
                    <span className="py-2 px-3 border border-yellow-500 text-yellow-500 rounded-3xl text-sm font-semibold">
                      {rootOrganization.displayName}
                    </span>
                  </div>
                )}
              </Drawer.Header>
            )}
            {!isEdit && (
              <div className="flex justify-between gap-4">
                <Drawer.Header className="!mb-5">
                  <Drawer.Title>{data?.organization.displayName}</Drawer.Title>
                  <Drawer.Description>{data?.organization.name}</Drawer.Description>
                  {isOU && rootOrganization && (
                    <div className="flex pt-4">
                      <span className="py-2 px-3 border border-yellow-500 text-yellow-500 rounded-3xl text-sm font-semibold">
                        {rootOrganization.displayName}
                      </span>
                    </div>
                  )}
                </Drawer.Header>

                {isAdmin && (
                  <div className="flex flex-grow justify-end gap-2 items-start">
                    <Button variant="primary" size="sm" onClick={() => handleInviteResearcher()}>
                      {'Invite Researcher'}
                    </Button>

                    <TooltipButton
                      tooltipText={t('edit_organization')}
                      onClick={() => setIsEdit(true)}
                    >
                      <EditIcon className="h-5 w-5" />
                    </TooltipButton>
                    <TooltipButton tooltipText={t('actions')} onClick={() => setIsMenuOpen(true)}>
                      <ActionIcon className="text-white" />
                    </TooltipButton>

                    {isMenuOpen && (
                      <>
                        <div className="absolute flex p-2 flex-col w-56 right-0 z-[50] rounded border border-gray-800 bg-gray-900">
                          <div
                            className="flex p-2 gap-2 items-center text-white cursor-pointer hover:opacity-90"
                            onClick={handleCreateStudy}
                          >
                            <StudyIcon className="w-4 h-4" /> {t('create_study')}
                          </div>
                        </div>
                        <div
                          className="fixed  h-screen top-0 left-0 w-screen z-[40]"
                          onClick={() => setIsMenuOpen(false)}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* General Information */}
            <div className="flex flex-col gap-4">
              <span className="text-white font-bold">{t('general_information')}</span>

              <div className="flex bg-gray-900 p-6 text-white rounded-lg gap-16">
                <div className="flex flex-col gap-2">
                  <span className="font-semibold">{t('creation_date')}</span>
                  <span className="text-gray-500 text-sm">
                    {dayjs(data.organization.createdAt).format('YYYY-MM-DD')}
                  </span>
                </div>
                {!isOU && (
                  <div className="flex flex-col gap-2">
                    <span className="font-semibold">{t('organizational_units_header')}</span>
                    <span className="text-gray-500 text-sm">
                      {data.organizationalUnits ? data.organizationalUnits.length : 0}
                    </span>
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <span className="font-semibold">{t('researchers_header')}</span>
                  <span className="text-gray-500 text-sm">
                    {data.researchers ? data.researchers.length : 0}
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  <span className="font-semibold">{t('studies_header')}</span>
                  <span className="text-gray-500 text-sm">
                    {data.studies ? data.studies.length : 0}
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  <span className="font-semibold">{t('data_sources_header')}</span>
                  <span className="text-gray-500 text-sm">
                    {data.dataSources ? data.dataSources.length : 0}
                  </span>
                </div>
                {!isOU && // only show orgWideAccess for root organizations
                  (isEdit ? (
                    <div className="flex flex-col gap-2">
                      <span className="font-semibold">{t('organization_wide_access')}</span>
                      <Toggle
                        value={orgWideAccess}
                        onChange={() => setConfirmOrgWideAccess(true)}
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <span className="font-semibold">{t('organization_wide_access')}</span>
                      <span className="text-gray-500 text-sm">
                        {data.organization.orgWideAccess ? t('enabled') : t('disabled')}
                      </span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Overview */}
            <div className="flex flex-grow flex-col gap-4">
              <span className="text-white font-bold">{t('organization_overview')}</span>

              <Tabs
                defaultValue={!isOU ? 'organizational-units' : 'researchers'}
                className="bg-gray-900 text-white rounded-lg"
              >
                <Tabs.List>
                  {!isOU && (
                    <Tabs.Trigger value="organizational-units">
                      {t('organizational_units_header')}
                    </Tabs.Trigger>
                  )}
                  <Tabs.Trigger value="researchers">{t('researchers_header')}</Tabs.Trigger>
                  <Tabs.Trigger value="admins">{t('admins_header')}</Tabs.Trigger>
                  <Tabs.Trigger value="studies">{t('studies_header')}</Tabs.Trigger>
                  <Tabs.Trigger value="datasources">{t('data_sources_header')}</Tabs.Trigger>
                </Tabs.List>
                <Tabs.Content value="organizational-units">
                  <OrganizationalUnitsTable
                    organizations={data.organizationalUnits ? data.organizationalUnits : []}
                  />
                </Tabs.Content>
                <Tabs.Content value="researchers">
                  <OrganizationResearchersTable
                    researchers={data.researchers ? data.researchers : []}
                    allowActions={isAdmin}
                  />
                </Tabs.Content>
                <Tabs.Content value="admins">
                  <OrganizationResearchersTable researchers={data.admins ? data.admins : []} />
                </Tabs.Content>
                <Tabs.Content value="studies">
                  <OrganizationStudiesTable studies={data.studies ? data.studies : []} />
                </Tabs.Content>
                <Tabs.Content value="datasources">
                  <OrganizationDataSourcesTable
                    dataSources={data.dataSources ? data.dataSources : []}
                  />
                </Tabs.Content>
              </Tabs>
            </div>
          </div>
        )}
        {confirmOrgWideAccess && (
          <Modal
            isOpen
            onCancel={() => setConfirmOrgWideAccess(false)}
            title={t('confirm_modifying_organization_wide_access')}
            actionType="primary"
            actionText={t('confirm')}
            actionFunction={() => {
              setOrgWideAccess(!orgWideAccess);
              setConfirmOrgWideAccess(false);
            }}
            container={container}
          >
            <div className="w-[512px]">
              {orgWideAccess ? (
                <p>{t('disable_organization_wide_access_confirmation')}</p>
              ) : (
                <p>{t('enable_organization_wide_access_confirmation')}</p>
              )}
              <p className="mt-2">{t('note_change_not_saved_until_click_save')}</p>
            </div>
          </Modal>
        )}
        <Outlet context={{ container }} />
      </Drawer.Content>
    </Drawer>
  );
};

export const useOrganizationDetailsContext = () => {
  return useOutletContext<OutletContextType>();
};
